import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { apiRequest } from '../../utils/apiHelper';

const EditDocument = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cases, setCases] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    doc_type: '',
    case: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [docResponse, casesResponse] = await Promise.all([
          apiRequest(`/api/documents/${id}/`),
          apiRequest('/api/cases/'),
        ]);

        setFormData({
          name: docResponse.name || '',
          description: docResponse.description || '',
          doc_type: docResponse.doc_type || '',
          case: docResponse.case || '',
        });

        const casesData = Array.isArray(casesResponse?.data)
          ? casesResponse.data
          : Array.isArray(casesResponse)
          ? casesResponse
          : [];
        setCases(casesData);
      } catch (err) {
        console.error('Failed to fetch document:', err);
        setError('Failed to load document details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchData();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Document name is required');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        doc_type: formData.doc_type,
      };

      if (formData.case) {
        payload.case = formData.case;
      } else {
        payload.case = null;
      }

      await apiRequest(`/api/documents/${id}/`, 'PATCH', payload);
      setSuccess('Document updated successfully.');
      setTimeout(() => navigate(`/documents/${id}`), 1200);
    } catch (err) {
      console.error('Failed to update document:', err);
      setError('Failed to update document. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-700/10 rounded-lg p-6">
      <div className="mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
        <Link
          to={`/documents/${id}`}
          className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center mb-2"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to Document
        </Link>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">Edit Document</h2>
      </div>

      {error && (
        <div className="mb-4 flex items-center p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md">
          <ExclamationCircleIcon className="h-5 w-5 text-red-500 mr-2" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-4 flex items-center p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-md">
          <CheckIcon className="h-5 w-5 text-green-500 mr-2" />
          <span className="text-sm text-green-700 dark:text-green-300">{success}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Document Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
          />
        </div>

        <div>
          <label htmlFor="doc_type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Document Type
          </label>
          <select
            id="doc_type"
            name="doc_type"
            value={formData.doc_type}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
          >
            <option value="">Select type</option>
            <option value="legal">Legal Document</option>
            <option value="contract">Contract</option>
            <option value="court">Court Filing</option>
            <option value="correspondence">Correspondence</option>
            <option value="evidence">Evidence</option>
            <option value="research">Research Document</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div>
          <label htmlFor="case" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Associated Case
          </label>
          <select
            id="case"
            name="case"
            value={formData.case}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm dark:bg-gray-700 dark:text-white"
          >
            <option value="">No case association</option>
            {cases.map((c) => (
              <option key={c.id} value={c.id}>
                {c.title}
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Link
            to={`/documents/${id}`}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditDocument;
